import React, { useEffect, useRef, useState } from "react";
import { ChatAction } from "../../../http";
import { useChatTabs } from "../hooks/useChatTabs";
import { useChatMessages } from "../hooks/useChatMessages";
import { renderMessage } from "../utils/chatbot.renderer";
import { ChatbotWindowProps } from "../types/chatbot.types";
import ScoreCard from "./ScoreCard";
import HospitalSearch from "./HospitalSearch";
import ImageUpload from "./ImageUpload";
import InsuranceUpload from "./InsuranceUpload";
import styles from "../styles/ChatbotWindow.module.css";

const ChatbotWindow: React.FC<ChatbotWindowProps> = ({ initialBenId, initialBenName, allBens, onClose }) => {
  const { tabs, setTabs, activeTabId, setActiveTabId, addNewSession, assignBenToTab, closeTab } =
    useChatTabs(allBens);
  const { sendMessage, fetchHistory, handleScroll, bottomRef, bodyRef, scrollToBottom } =
    useChatMessages(setTabs);

  const [pendingUploadKey, setPendingUploadKey] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];

  useEffect(() => {
    setActiveTabId(tabs[0].id);
    tabs.forEach((tab) => {
      if (tab.beneficiaryId && tab.sessionId) fetchHistory(tab);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!activeTab?.loading) inputRef.current?.focus();
  }, [activeTab?.loading]);

  useEffect(() => {
    scrollToBottom();
  }, [activeTab?.messages]); // eslint-disable-line

  const handleActionClick = (action: ChatAction) => {
    if (action.actionKey.startsWith("UPLOAD_FILE:")) {
      setPendingUploadKey(action.actionKey);
      fileInputRef.current?.click();
    } else if (action.actionKey !== "SEARCH_HOSPITAL") {
      sendMessage(activeTab.id, activeTab, action.actionKey);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUploadKey) return;
    sendMessage(activeTab.id, activeTab, `FILE_SELECTED:${file.name}`);
    setPendingUploadKey(null);
    e.target.value = "";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || activeTab.loading) return;
    sendMessage(activeTab.id, activeTab, input.trim());
    setInput("");
  };

  return (
    <div className={styles.window}>
      <div className={styles.header}>
        <span>AI Assistant</span>
        <button className={styles.closeBtn} onClick={onClose}>✕</button>
      </div>

      <div className={styles.tabBar}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`${styles.tab} ${tab.id === activeTabId ? styles.tabActive : ""}`}
            onClick={() => setActiveTabId(tab.id)}
          >
            <span className={styles.tabName}>{tab.benName}</span>
            {tabs.length > 1 && (
              <span className={styles.tabClose} onClick={(e) => { e.stopPropagation(); closeTab(tab.id, onClose); }}>✕</span>
            )}
          </div>
        ))}
        {tabs.length < 3 && (
          <button className={styles.newSessionBtn} onClick={addNewSession}>+ New Session</button>
        )}
      </div>

      <div className={styles.body} ref={bodyRef} onScroll={() => handleScroll(activeTab)}>
        {!activeTab.beneficiaryId && (
          <div className={styles.benPickerBody}>
            <p className={styles.benPickerTitle}>Select a profile to start</p>
            {allBens.map((ben) => (
              <button
                key={ben._id}
                className={styles.benPickerItem}
                onClick={() => assignBenToTab(activeTab.id, ben, (tabId, tab) => sendMessage(tabId, tab, "Start"))}
              >
                <div className={styles.benPickerInfo}>
                  <span>{ben.name}</span>
                  <span className={styles.benPickerRel}>{ben.relationship}</span>
                </div>
                {ben.overallScore !== undefined && (
                  <span
                    className={styles.benPickerScore}
                    style={{ color: ben.overallScore >= 90 ? "#008470" : ben.overallScore >= 50 ? "#F68524" : "#EA1515" }}
                  >
                    {ben.overallScore}%
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {activeTab.beneficiaryId && activeTab.loading && activeTab.messages.length === 0 && (
          <div className={styles.historyLoader}>Loading...</div>
        )}

        {activeTab.messages.map((msg, i) => (
          <div key={i} className={msg.from === "user" ? styles.userMsg : styles.botMsg}>
            <p>{renderMessage(msg.text, styles.mdTable)}</p>
            {msg.indexScores && <ScoreCard scores={msg.indexScores} />}
            {msg.actions && msg.actions.length > 0 && (
              <div className={styles.actions}>
                {msg.actions.map((action) => {
                  const coords =
                    action.metadata?.latitude && action.metadata?.longitude
                      ? { lat: action.metadata.latitude, lng: action.metadata.longitude }
                      : null;
                  if (action.actionKey === "SEARCH_HOSPITAL") {
                    return (
                      <HospitalSearch
                        key={action.actionKey}
                        lat={coords?.lat}
                        lng={coords?.lng}
                        onSelect={(id, name, distance) =>
                          sendMessage(activeTab.id, activeTab, `HOSPITAL_SELECTED:${id}:${name}:${distance?.toFixed(2)}km`)
                        }
                      />
                    );
                  }
                  if (action.actionKey === "IMAGE_UPLOAD") {
                    return (
                      <ImageUpload
                        key={action.actionKey}
                        beneficiaryId={activeTab.beneficiaryId}
                        onUpload={() => sendMessage(activeTab.id, activeTab, "IMAGE_UPLOADED_SUCCESSFULLY")}
                      />
                    );
                  }
                  if (action.actionKey === "INSURANCE_IMAGE_UPLOAD") {
                    return (
                      <InsuranceUpload
                        key={action.actionKey}
                        beneficiaryId={activeTab.beneficiaryId}
                        onUpload={(url) => sendMessage(activeTab.id, activeTab, `INSURANCE_IMAGE_UPLOADED:${url}`)}
                      />
                    );
                  }
                  return (
                    <button key={action.actionKey} className={styles.actionBtn} onClick={() => handleActionClick(action)}>
                      {action.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {activeTab.loading && activeTab.messages.length > 0 && (
          <div className={styles.botMsg}>
            <div className={styles.typingIndicator}>
              <span className={styles.typingDot} />
              <span className={styles.typingDot} />
              <span className={styles.typingDot} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form className={styles.footer} onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          className={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message..."
          disabled={activeTab.loading}
        />
        <button className={styles.sendBtn} type="submit" disabled={activeTab.loading || !input.trim()}>
          Send
        </button>
      </form>

      <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleFileChange} />
    </div>
  );
};

export default ChatbotWindow;
